import * as THREE from 'three';

/**
 * Animation control utilities for particle systems
 */

// Store animation IDs for each particle system
const animationIds = new Map();

/**
 * Start animation for a particle system
 * @param {THREE.Object3D} particleSystem - The particle system object
 * @param {Function} animateFunction - The animation function
 * @param {string} particleId - The particle ID
 */
export const startAnimation = (particleSystem, animateFunction, particleId) => {
  if (!particleSystem || !animateFunction) return;
  // Stop existing animation if any
  stopAnimation(particleSystem);

  // Start new animation
  const animationId = requestAnimationFrame(animateFunction);
  particleSystem.userData.animationId = animationId;
  animationIds.set(particleId, animationId);
};

/**
 * Stop animation for a particle system
 * @param {THREE.Object3D} particleSystem - The particle system object
 * @param {string} particleId - The particle ID
 */
export const stopAnimation = (particleSystem, particleId) => {
  if (!particleSystem) return;

  // Cancel current animation
  if (particleSystem.userData.animationId) {
    cancelAnimationFrame(particleSystem.userData.animationId);
    particleSystem.userData.animationId = null;
  }

  // Remove from tracking map
  if (particleId && animationIds.has(particleId)) {
    animationIds.delete(particleId);
  }
};

/**
 * Pause animation for a particle system
 * @param {THREE.Object3D} particleSystem - The particle system object
 * @param {string} particleId - The particle ID
 */
export const pauseAnimation = (particleSystem, particleId) => {
  if (!particleSystem) return;

  // Cancel current animation but keep the system ready to resume
  if (particleSystem.userData.animationId) {
    cancelAnimationFrame(particleSystem.userData.animationId);
    particleSystem.userData.animationId = null;
  }
};

/**
 * Resume animation for a particle system
 * @param {THREE.Object3D} particleSystem - The particle system object
 * @param {Function} animateFunction - The animation function
 * @param {string} particleId - The particle ID
 */
export const resumeAnimation = (
  particleSystem,
  animateFunction,
  particleId
) => {
  if (!particleSystem || !animateFunction) return;

  // Start animation again
  const animationId = requestAnimationFrame(animateFunction);
  particleSystem.userData.animationId = animationId;
  animationIds.set(particleId, animationId);
};

/**
 * Reset particle system to initial state
 * @param {THREE.Object3D} particleSystem - The particle system object
 * @param {string} particleId - The particle ID
 */
export const resetParticleSystem = (particleSystem, particleId) => {
  if (!particleSystem) return;

  // Stop animation
  stopAnimation(particleSystem, particleId);

  // Reset particle positions based on type
  if (particleSystem.objType === 'snow') {
    resetSnowParticles(particleSystem);
  } else if (particleSystem.objType === 'rain') {
    resetRainParticles(particleSystem);
  } else if (particleSystem.objType === 'fire') {
    resetFireParticles(particleSystem);
  }
  // Add more particle types as needed
};

/**
 * Reset snow particles to initial positions
 * @param {THREE.Object3D} snowSystem - The snow particle system
 */
const resetSnowParticles = (snowSystem) => {
  const snowPoints = snowSystem.children.find(
    (child) => child instanceof THREE.Points
  );
  if (!snowPoints || !snowPoints.geometry) return;

  const positions = snowPoints.geometry.attributes.position.array;
  const config = snowSystem.userData;
  const currentRange = config.range !== undefined ? config.range : 100;
  const currentHeight = config.height !== undefined ? config.height : 50;

  for (let i = 0; i < positions.length; i += 3) {
    positions[i] = (Math.random() - 0.5) * currentRange;
    positions[i + 1] = Math.random() * currentHeight;
    positions[i + 2] = (Math.random() - 0.5) * currentRange;
  }

  snowPoints.geometry.attributes.position.needsUpdate = true;
  snowPoints.geometry.computeBoundingBox();
  snowPoints.geometry.computeBoundingSphere();
  
  if (snowSystem.userData) snowSystem.userData.forceBoxUpdate = true;
};

/**
 * Reset rain particles to initial positions
 * @param {THREE.Object3D} rainSystem - The rain particle system
 */
const resetRainParticles = (rainSystem) => {
  const rainPoints = rainSystem.children.find(
    (child) => child instanceof THREE.Points
  );
  if (!rainPoints || !rainPoints.geometry) return;

  const positions = rainPoints.geometry.attributes.position.array;
  const config = rainSystem.userData;
  const currentRange = config.range !== undefined ? config.range : 100;
  const currentHeight = config.height !== undefined ? config.height : 50;

  for (let i = 0; i < positions.length; i += 3) {
    positions[i] = (Math.random() - 0.5) * currentRange;
    positions[i + 1] = Math.random() * currentHeight;
    positions[i + 2] = (Math.random() - 0.5) * currentRange;
  }

  rainPoints.geometry.attributes.position.needsUpdate = true;
  rainPoints.geometry.computeBoundingBox();
  rainPoints.geometry.computeBoundingSphere();
  
  if (rainSystem.userData) rainSystem.userData.forceBoxUpdate = true;
};

/**
 * Reset fire particles to initial positions
 * @param {THREE.Object3D} fireSystem - The fire particle system
 */
const resetFireParticles = (fireSystem) => {
  // Reset core flames
  const coreFlames = fireSystem.children.find(
    (child) => child.name === 'coreFlames'
  );
  if (coreFlames && coreFlames.geometry) {
    const positions = coreFlames.geometry.attributes.position.array;
    const config = fireSystem.userData;

    for (let i = 0; i < positions.length; i += 3) {
      positions[i] = (Math.random() - 0.5) * (config.range || 2);
      positions[i + 1] = Math.random() * (config.range || 2);
      positions[i + 2] = (Math.random() - 0.5) * (config.range || 2);
    }

    coreFlames.geometry.attributes.position.needsUpdate = true;
    coreFlames.geometry.computeBoundingBox();
    coreFlames.geometry.computeBoundingSphere();
    
    if (fireSystem.userData) fireSystem.userData.forceBoxUpdate = true;
  }

  // Reset embers
  const embers = fireSystem.children.find((child) => child.name === 'embers');
  if (embers && embers.geometry) {
    const positions = embers.geometry.attributes.position.array;
    const config = fireSystem.userData;

    for (let i = 0; i < positions.length; i += 3) {
      positions[i] = (Math.random() - 0.5) * (config.range || 2);
      positions[i + 1] = Math.random() * (config.range || 2);
      positions[i + 2] = (Math.random() - 0.5) * (config.range || 2);
    }

    embers.geometry.attributes.position.needsUpdate = true;
    embers.geometry.computeBoundingBox();
    embers.geometry.computeBoundingSphere();
    
    if (fireSystem.userData) fireSystem.userData.forceBoxUpdate = true;
  }
};

/**
 * Get animation state for a particle system
 * @param {THREE.Object3D} particleSystem - The particle system object
 * @returns {Object} Animation state {isPlaying, isStopped}
 */
export const getAnimationState = (particleSystem) => {
  if (!particleSystem) return { isPlaying: false, isStopped: true };

  const hasAnimation =
    particleSystem.userData.animationId !== null &&
    particleSystem.userData.animationId !== undefined;

  return {
    isPlaying: hasAnimation,
    isStopped: !hasAnimation,
  };
};

/**
 * Clean up all animations (useful for scene cleanup)
 */
export const cleanupAllAnimations = () => {
  animationIds.forEach((animationId) => {
    cancelAnimationFrame(animationId);
  });
  animationIds.clear();
};
